import { useMemo } from 'react'

import Company from '~/client/src/shared/models/Company'
import User from '~/client/src/shared/models/User'
import UserProject from '~/client/src/shared/models/UserProject'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectTradesStore from '~/client/src/shared/stores/domain/ProjectTrades.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import getCompanyTypeTranslate from '~/client/src/shared/utils/getCompanyTypeTranslate'

export interface ICompanyMetadata {
  typeTagNames?: string[]
  tradeNames?: string[]
}

export interface ICurrentCompanyInfo {
  companyId: string
}

interface IStores {
  companiesStore: CompaniesStore
  projectTradesStore: ProjectTradesStore
  userProjectsStore?: UserProjectsStore
  currentUser?: User
}

export function useCompanyListSelectorData(stores: IStores): {
  companies: Company[]
  companyMetadata: Map<string, ICompanyMetadata>
  currentCompany: ICurrentCompanyInfo | undefined
} {
  const { companiesStore, projectTradesStore, userProjectsStore, currentUser } =
    stores

  const companies = companiesStore.availableSortedCompanies

  const companyMetadata = useMemo(() => {
    const map = new Map<string, ICompanyMetadata>()
    for (const company of companies) {
      const typeTags = companiesStore.getCompanyTypeTagsByIds(
        company.typeTags || [],
      )
      const typeTagNames = typeTags
        ?.map(tag => (tag?.value ? getCompanyTypeTranslate(tag.value) : null))
        ?.filter(Boolean) as string[]

      const tradeNames = (company.tradeIds || [])
        .map(id => projectTradesStore?.getInstanceById(id)?.name)
        .filter(Boolean) as string[]

      map.set(company.id, {
        typeTagNames: typeTagNames?.length ? typeTagNames : undefined,
        tradeNames: tradeNames?.length ? tradeNames : undefined,
      })
    }
    return map
  }, [companies, companiesStore, projectTradesStore])

  const currentCompany = useMemo((): ICurrentCompanyInfo | undefined => {
    if (!currentUser || !userProjectsStore) return undefined
    const companyId = UserProject.getCompanyId(currentUser, userProjectsStore)
    return companyId ? { companyId } : undefined
  }, [currentUser, userProjectsStore])

  return { companies, companyMetadata, currentCompany }
}
